import {ErrorHandler, inject, Injectable} from '@angular/core';
import {MessagingService} from "../messaging/messaging.service";
import {HttpErrorResponse} from "@angular/common/http";
import {Router} from "@angular/router";
import {SessionViewmodelService} from "../../../viewmodel/session-viewmodel.service";
import {RateLimitNoticeService} from "./rate-limit-notice.service";

@Injectable({
    providedIn: 'root'
})
export class PtmErrorHandlerService implements ErrorHandler {

    router = inject(Router);
    sessionViewmodelService = inject(SessionViewmodelService);
    messagingService = inject(MessagingService);
    rateLimitNotice = inject(RateLimitNoticeService);

    constructor() {
    }

    handleError(error: any): void {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
                // 401 só significa "credenciais inválidas" no PRÓPRIO login; em
                // qualquer outra rota autenticada é sessão expirada (polimento
                // UX 2026-07-03 - antes exibia "usuário/senha inválidos" fora
                // de contexto).
                if (error.url?.includes('/auth/login')) {
                    this.messagingService.addDanger('Usuário e/ou senha inválido(s).');
                } else if (this.router.url.startsWith('/admin')) {
                    this.encerrarSessao();
                } else {
                    this.messagingService.addDanger(
                        'Algo deu errado ao processar sua solicitação. Tente novamente em instantes.',
                        'Não foi possível concluir.');
                }
            } else if (error.status === 403) {
                // Antes: logout+redirect SILENCIOSO - o operador era "deslogado
                // do nada" sem explicação (polimento UX 2026-07-03).
                this.encerrarSessao();
            } else if (error.status === 413) {
                // 413 Payload Too Large - pode vir do Laravel (JSON {message}) ou do nginx (HTML
                // sem JSON). Mensagem fallback cobre o caso nginx.
                this.messagingService
                    .addDanger(
                        error.error?.message ?? 'O arquivo excede o tamanho máximo permitido pelo servidor. Reduza o arquivo e tente novamente.',
                        'Arquivo muito grande.');
            } else if (error.status === 429) {
                // 429 Too Many Requests (thread 0044). API entrega header `Retry-After`
                // sempre presente após fix do `ExceptionHandler::error()` (commit 737d51bc).
                // Mensagem PT-BR canônica vem em `error.error.message` ("Você fez muitas...");
                // fallback genérico cobre fonte sem JSON.
                const retryAfter = this.parseRetryAfter(error);
                const message = error.error?.message ?? 'Você fez muitas solicitações em pouco tempo. Aguarde alguns segundos e tente novamente.';
                this.rateLimitNotice.show(message, retryAfter);
                // Log estruturado sem PII (status, url, retry-after) - visibilidade pós-deploy.
                console.warn('[429 rate limited]', {
                    url: error.url,
                    retryAfterSeconds: retryAfter,
                });
            } else if (!!error.error?.message) {
                // Mensagem da API direto, sem moldura burocrática/aspas aninhadas
                // e sem "procure o administrador" (quem?) - polimento UX 2026-07-03.
                this.messagingService
                    .addDanger(error.error.message, 'Não foi possível concluir.');
            } else {
                this.messagingService
                    .addDanger('Algo deu errado ao processar sua solicitação. Tente novamente em instantes.',
                        'Não foi possível concluir.');
            }
        } else {
            console.log(error);
            this.messagingService
                .addDanger('Algo deu errado ao processar sua solicitação. Tente novamente em instantes.',
                    'Não foi possível concluir.');
        }
    }

    /** 401 (sessão expirada) e 403: encerra e explica APÓS o redirect - a
     *  mensagem precisa nascer quando o <ptm-messaging> da tela de login já
     *  está inscrito, senão se perde no Subject. */
    private encerrarSessao(): void {
        this.sessionViewmodelService.logout();
        this.router.navigate(['admin']).then(() =>
            this.messagingService.addDanger(
                'Sua sessão expirou ou você não tem permissão para esta ação. Entre novamente.',
                'Sessão encerrada.'));
    }

    /** `Retry-After` em segundos (HTTP delay-seconds form). Fallback 60s. */
    private parseRetryAfter(error: HttpErrorResponse): number {
        const raw = error.headers?.get('Retry-After');
        if (!raw) return 60;
        const seconds = parseInt(raw, 10);
        return Number.isFinite(seconds) && seconds > 0 ? seconds : 60;
    }
}
